/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { Switch } from 'react-router-dom';
import { PrivateRoute } from '@routes/privateRoute';
import { Header } from '@components/nlp-portal/header/header';
import { DashboardPage } from '@content-components/dashboard/dashboardPage';
import { UsersPage } from '@content-components/users/usersPage';
import { UserPage } from '@content-components/users/detail/userPage';
import { ByMessagePage } from '@content-components/conversation-history/messages/byMesagePage';
import { ByIntentPage } from '@content-components/conversation-history/intents/byIntentPage';
import { DarkZonePage } from '@content-components/conversation-history/tech-zone/darkZonePage';
import { DomainsPage } from '@content-components/manage-the-bot/domains/domainsPage';
import { DomainPage } from '@content-components/manage-the-bot/domains/detail/domainPage';
import { IntentsPage } from '@content-components/manage-the-bot/intents/intentsPage';
import { IntentPage } from '@content-components/manage-the-bot/intents/detail/intentPage';
import { FlowsPage } from '@content-components/manage-the-bot/flows/flowsPage';
import { FlowPage } from '@content-components/manage-the-bot/flows/detail/flowPage';
import { LastTrainingPage } from '@content-components/manage-the-bot/smart-coach/lastTrainingPage';
import { BotSettingsPage } from '@components/nlp-portal/content/manage-the-bot/bot-settings/botSettingsPage';
import { VDNSettingsPage } from '@components/nlp-portal/content/manage-the-bot/vdn-setting/vdnSettingsPage';
import { ReleasesPage } from '@content-components/manage-the-bot/releases/releasesPage';
import { SupportPage } from '@content-components/support/supportPage';
import { NotFound404Page } from '@components/not-found/notFound404Page';
import { ChatbotButton } from '@common-components/widget/chatbotButton';
import { ScrollToTop } from '@common-components/scrollToTop/scrollToTop';
import { isUserAbleToSeeThis, isUserAbleToSeeUsersPage } from '@utils/authentication';
import { ROUTES } from '@constants';

export const AuthenticatedRoutes = () => {
  return (
    <>
      <Header />
      <main className="main-content block" role="main">
        <ScrollToTop />
        <Switch>
          <PrivateRoute exact path={ROUTES.dashboard.path} component={DashboardPage} />
          <PrivateRoute exact path={ROUTES.conversationHistory.byMessage.path} component={ByMessagePage} />
          <PrivateRoute exact path={ROUTES.conversationHistory.byIntent.path} component={ByIntentPage} />
          {isUserAbleToSeeThis('dark-zone:view') && (
            <PrivateRoute exact path={ROUTES.conversationHistory.darkZone.path} component={DarkZonePage} />
          )}

          <PrivateRoute exact path={ROUTES.manageBot.domains.path} component={DomainsPage} />
          <PrivateRoute exact path={ROUTES.manageBot.corpus.path} component={DomainsPage} />
          <PrivateRoute path={ROUTES.manageBot.newDomain.path}>
            <DomainPage editMode={false} />
          </PrivateRoute>
          <PrivateRoute path={ROUTES.manageBot.editDomain.path}>
            <DomainPage editMode />
          </PrivateRoute>
          <PrivateRoute path={ROUTES.manageBot.readOnlyDomain.path}>
            <DomainPage editMode readOnly />
          </PrivateRoute>
          {isUserAbleToSeeUsersPage() && <PrivateRoute exact path={ROUTES.users.path} component={UsersPage} />}
          {isUserAbleToSeeUsersPage() && (
            <PrivateRoute path={ROUTES.addUser.path}>
              <UserPage editMode={false} />
            </PrivateRoute>
          )}
          {isUserAbleToSeeUsersPage() && (
            <PrivateRoute path={ROUTES.editUser.path}>
              <UserPage editMode />
            </PrivateRoute>
          )}
          <PrivateRoute path={ROUTES.manageBot.intentsByDomain.path}>
            <IntentsPage byDomain />
          </PrivateRoute>
          <PrivateRoute exact path={ROUTES.manageBot.intents.path} component={IntentsPage} />
          <PrivateRoute path={ROUTES.manageBot.newIntent.path}>
            <IntentPage editMode={false} />
          </PrivateRoute>
          <PrivateRoute path={ROUTES.manageBot.editIntent.path}>
            <IntentPage editMode />
          </PrivateRoute>
          <PrivateRoute path={ROUTES.manageBot.readOnlyIntent.path}>
            <IntentPage editMode readOnly />
          </PrivateRoute>
          <PrivateRoute exact path={ROUTES.manageBot.manageFlow.path} component={FlowsPage} />

          <PrivateRoute exact path={ROUTES.manageBot.newFlow.path} component={FlowPage}>
            <FlowPage editMode={false} />
          </PrivateRoute>
          <PrivateRoute exact path={ROUTES.manageBot.editFlow.path} component={FlowPage}>
            <FlowPage editMode />
          </PrivateRoute>
          <PrivateRoute exact path={ROUTES.manageBot.readOnlyFlow.path} component={FlowPage}>
            <FlowPage editMode readOnly />
          </PrivateRoute>
          {/* Route to use in intent link at conversation-detail and Test the intent widget */}
          <PrivateRoute path={ROUTES.manageBot.directIntentLink.path}>
            <IntentPage editMode />
          </PrivateRoute>

          <PrivateRoute exact path={ROUTES.manageBot.lastTraining.path} component={LastTrainingPage} />
          <PrivateRoute exact path={ROUTES.manageBot.releases.path} component={ReleasesPage} />
          {isUserAbleToSeeThis('bot-settings:view') && (
            <PrivateRoute exact path={ROUTES.manageBot.botConnection.path} component={BotSettingsPage} />
          )}
          {isUserAbleToSeeThis('bot-settings:view') && (
            <PrivateRoute exact path={ROUTES.manageBot.vdnSettings.path} component={VDNSettingsPage} />
          )}

          <PrivateRoute exact path={ROUTES.support.path} component={SupportPage} />
          <PrivateRoute component={NotFound404Page} />
        </Switch>
        <ChatbotButton />
      </main>
    </>
  );
};
